/**
 * @file The two Firestore stores one Signify fetch job is followed through: the job document and
 * its `parts` subcollection. Consumers: `useSignifyFetchJobModal`, which connects and disconnects
 * them, and every component under the progress step, which calls this for the instance and reads.
 * The query options are spelled here once because `createCollectionStore` keys its registry on the
 * canonicalized options — two call sites spelling them differently open two listeners over the same
 * data. The `userId` filter is not an optimization: `firestore.rules` admits a list on `parts` only
 * when the query provably restricts `userId`, so without it the listener fails permission-denied.
 * Components under the modal never call `connect`; the always-mounted host hook owns the lifecycle,
 * which is what keeps a job alive while its modal is closed.
 */

import { signifyFetchJobPartsPath, signifyFetchJobPath } from '#types';
import type { SignifyFetchJob, SignifyFetchPart } from '#types';
import { createCollectionStore, createDocumentStore } from '@/store';
import type { CollectionStore, DocumentStore } from '@/store';

export type SignifyFetchJobStores = {
  job: DocumentStore<SignifyFetchJob>;
  parts: CollectionStore<SignifyFetchPart>;
};

export const createSignifyFetchJobStores = (jobId: string, userId: string): SignifyFetchJobStores => ({
  job: createDocumentStore<SignifyFetchJob>(signifyFetchJobPath(jobId)),
  parts: createCollectionStore<SignifyFetchPart>(signifyFetchJobPartsPath(jobId), {
    queryOptions: {
      orderBy: { field: 'order', direction: 'asc' },
      extraConstraints: [{ type: 'where', field: 'userId', op: '==', value: userId }],
    },
  }),
});
